/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
/**
 * This is where named components should be registered, for example:
 *
 * <code>
 * import { services } from '@broadleaf/admin-components';
 * const { ComponentRegistrar } = services;
 *
 * ComponentRegistrar.registerComponent('blButton', MyCustomButton);
 *
 * // override a Field type
 * ComponentRegistrar.registerFieldComponent('STRING', NewStringField);
 *
 * // add a new Field type
 * ComponentRegistrar.registerFieldComponent('CUSTOM', MyCustomField);
 *
 * // register a new View type
 * ComponentRegistrar.registerViewComponent('MY_VIEW', MyView);
 * </code>
 */
import CalculatedPriceField from '@src/components/CalculatedPriceField';
import CalculatedVendorRefField from '@src/components/CalculatedVendorRefField';
import CalculatedNetworkRefField from '@src/components/CalculatedNetworkRefField';
import ProductInfoField from '@src/components/ProductInfoField';
import RmStringArrayField from '@src/components/field/RmStringArrayField';
import ProductInfoDisplay from '@src/components/ProductInfoDisplay';
import ScanditBarcodeScannerField from '@src/components/form/barcode/ScanditBarcodeScannerField';
import BulkScanditBarcodeScannerField from '@src/components/form/barcode/BulkScanditBarcodeScannerField';
import BulkScanResidentMapField from '@src/components/form/BulkScanResidentMapField/BulkScanResidentMapField';
import bulkScanResidentMapActions from '@src/components/form/BulkScanResidentMapField/action-components';
import ImpersonateRevMedUser from '@src/components/form/EntityForm/actions/ImpersonateRevMedUser';
import BulkScanSummaryColumn from '@src/components/form/column-types/BulkScanSummaryColumn';
import RawEnumColumn from '@src/components/form/column-types/RawEnumColumn';
import LabeledBooleanField from '@src/components/LabeledBooleanField';
import RmMoneyField from '@src/components/RmMoneyField';
import RMEnumSwitchField from '@src/components/form/toggle-switch/RMEnumSwitchField';
import LinkColumn from '@src/components/LinkColumn';
import LookupField from '@src/components/form/lookup/LookupField';
import Header from '@src/components/view/Header';
import Navigation from '@src/components/view/navigation/Navigation';
import RMButton from '@src/components/RMButton';
import RMToggleSwitch from '@src/components/form/toggle-switch/RMToggleSwitch';
import RmFulfillmentView from '@src/oms/FulfillmentView/RmFulfillmentView';
import LinkButton from '@src/components/form/EntityGrid/actions/LinkButton';
import InlineGroup from '@src/components/form/group-types/InlineGroup';
import VariantIdentificationGroup from '@src/components/form/group-types/VariantIdentificationGroup';
import { ComponentRegistrar } from '@broadleaf/admin-components/dist/common';
import { ActionRegistrar } from '@broadleaf/admin-components/dist/metadata';
import { InvoicesView } from '@src/oms/InvoicesView/index';
import { OpenRemitraGrid } from '@src/oms/InvoicesView/components/OpenRemitraGrid';
import { InvoiceSubmissionHistoryGrid } from '@src/oms/InvoicesView/components/InvoiceSubmissionHistoryGrid';
import { OrderProcessingGrid } from '@src/oms/InvoicesView/components/OrderProcessingGrid';
import { OtherInvoicesGrid } from '@src/oms/InvoicesView/components/OtherInvoicesGrid';
import { HeldInvoicesGrid } from '@src/oms/InvoicesView/components/HeldInvoicesGrid';
import RmPercentageField from '@src/components/RmPercentageField';
import RmPercentageColumn from '@src/components/RmPercentageColumn';
import RmItemsOrderViewSection from '@src/oms/ItemsOrderViewSection/RmItemsOrderViewSection';
import { ExternalAssetDropzone } from '@src/components/named-components/external-types/ExternalAssetDropzone';
import { AssetDropzoneField } from '@src/components/named-components/field-types/AssetDropzoneField';
import AssetBrowserView from '@src/components/named-components/view-types/AssetBrowserView';
import { OpenRevmedGrid } from '@src/oms/InvoicesView/components/OpenRevmedGrid';
import { OpenExternalGrid } from '@src/oms/InvoicesView/components/OpenExternalGrid';
import ActionListGridImpersonateAction from '@src/components/named-components/field-types/ActionListGridImpersonateAction';
import PricingRequestWorksheetAction from '@src/components/common/components/PricingRequestWorksheetAction';
import DownloadPricingActivityAction from '@src/components/named-components/field-types/DownloadPricingActivityAction';
import ConditionalTriggerAction from '@src/components/common/components/ConditionalTriggerAction';

// register group components
ComponentRegistrar.registerGroupComponent('INLINE', InlineGroup);
ComponentRegistrar.registerGroupComponent(
  'VARIANT_IDENTIFICATION',
  VariantIdentificationGroup
);

// Register the custom ProductInfo field
ComponentRegistrar.registerFieldComponent(
  'PRODUCT_INFO',
  ProductInfoField(ProductInfoDisplay)
);
// Register the custom CalculatedPrice field
ComponentRegistrar.registerFieldComponent(
  'CALCULATED_PRICE',
  CalculatedPriceField
);
ComponentRegistrar.registerFieldComponent(
  'CALCULATED_VENDOR_REF',
  CalculatedVendorRefField
);
ComponentRegistrar.registerFieldComponent(
  'CALCULATED_NETWORK_REF',
  CalculatedNetworkRefField
);

ComponentRegistrar.registerFieldComponent('MONEY', RmMoneyField);
ComponentRegistrar.registerFieldComponent('PERCENTAGE', RmPercentageField);
ComponentRegistrar.registerColumnComponent('PERCENTAGE', RmPercentageColumn);
ComponentRegistrar.registerFieldComponent('BOOLEAN', LabeledBooleanField);
ComponentRegistrar.registerFieldComponent('STRING_ARRAY', RmStringArrayField);

ComponentRegistrar.registerFieldComponent('ENUM_SWITCH', RMEnumSwitchField);

ComponentRegistrar.registerColumnComponent('LINK', LinkColumn);

ComponentRegistrar.registerFieldComponent('LIST_PRODUCT_LOOKUP', LookupField);

ComponentRegistrar.registerFieldComponent('ASSET_DROPZONE', AssetDropzoneField);
// Register the barcode scanner field
ComponentRegistrar.registerFieldComponent(
  'QUAGGA_BARCODE_SCANNER_FIELD',
  ScanditBarcodeScannerField()
);
ComponentRegistrar.registerFieldComponent(
  'SCANDIT_BARCODE_SCANNER_FIELD',
  ScanditBarcodeScannerField()
);
ComponentRegistrar.registerFieldComponent(
  'BULK_SCANDIT_BARCODE_SCANNER_FIELD',
  BulkScanditBarcodeScannerField()
);
ComponentRegistrar.registerComponent('blHeader', Header);
ComponentRegistrar.registerComponent('blNavigation', Navigation);

ComponentRegistrar.registerComponent('blButton', RMButton);

ComponentRegistrar.registerComponent('blToggleSwitch', RMToggleSwitch);

ComponentRegistrar.registerViewComponent(
  RmFulfillmentView.TYPE,
  RmFulfillmentView
);

ActionRegistrar.registerActionComponents(
  'View',
  RmFulfillmentView.TYPE,
  RmFulfillmentView.actionComponents
);

ActionRegistrar.registerActionComponent('View', 'ENTITY', {
  placement: 'PRIMARY',
  type: 'CONDITIONAL_TRIGGER',
  component: ConditionalTriggerAction
});

ActionRegistrar.registerActionComponent('View', 'ENTITY', {
  placement: 'SECONDARY',
  type: 'CONDITIONAL_TRIGGER',
  component: ConditionalTriggerAction
});

ComponentRegistrar.registerViewComponent('ASSET_BROWSER', AssetBrowserView);

ComponentRegistrar.registerViewComponent(
  RmItemsOrderViewSection.TYPE,
  RmItemsOrderViewSection
);

ActionRegistrar.registerActionComponents(
  'View',
  RmItemsOrderViewSection.TYPE,
  RmItemsOrderViewSection.actionComponents
);

RmItemsOrderViewSection.namedComponentOverrides.forEach(override => {
  ComponentRegistrar.registerComponent(override.type, override.component);
});

ComponentRegistrar.registerViewComponent('INVOICES', InvoicesView);

ActionRegistrar.registerActionComponents(
  'View',
  OpenRemitraGrid.VIEW_TYPE,
  OpenRemitraGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  OpenExternalGrid.VIEW_TYPE,
  OpenExternalGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  OpenRevmedGrid.VIEW_TYPE,
  OpenRevmedGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  HeldInvoicesGrid.VIEW_TYPE,
  HeldInvoicesGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  InvoiceSubmissionHistoryGrid.VIEW_TYPE,
  InvoiceSubmissionHistoryGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  OrderProcessingGrid.VIEW_TYPE,
  OrderProcessingGrid.actionComponents
);
ActionRegistrar.registerActionComponents(
  'View',
  OtherInvoicesGrid.VIEW_TYPE,
  OtherInvoicesGrid.actionComponents
);

ActionRegistrar.registerActionComponent('View', 'ENTITY_GRID', {
  placement: 'GRID',
  type: 'BULK_SCAN',
  component: LinkButton
});

ActionRegistrar.registerActionComponent('View', 'ENTITY', {
  placement: 'SECONDARY',
  type: 'IMPERSONATE_REVMED_USER',
  component: ImpersonateRevMedUser
});

ComponentRegistrar.registerFieldComponent(
  'BULK_SCAN_RESIDENT_MAP',
  BulkScanResidentMapField
);
ActionRegistrar.registerActionComponents(
  'Field',
  'BULK_SCAN_RESIDENT_MAP',
  bulkScanResidentMapActions
);

ComponentRegistrar.registerColumnComponent('RAW_ENUM', RawEnumColumn);
ComponentRegistrar.registerColumnComponent(
  'BULK_SCAN_SUMMARY',
  BulkScanSummaryColumn
);

ComponentRegistrar.registerExternalComponent(
  'ASSET_DROPZONE',
  ExternalAssetDropzone
);

ActionRegistrar.registerActionComponent('View', 'ENTITY_GRID', {
  placement: 'ROW',
  type: 'IMPERSONATE',
  component: ActionListGridImpersonateAction
});

ActionRegistrar.registerActionComponent('Field', 'RESIDENT_GRID', {
  placement: 'ROW',
  type: 'DOWNLOAD_PRICING_ACTIVITY',
  component: DownloadPricingActivityAction
});

ActionRegistrar.registerActionComponent('View', 'ENTITY', {
  placement: 'PRIMARY',
  type: 'PRICING_REQUEST_WORKSHEET',
  component: PricingRequestWorksheetAction
});
